import React, { useState } from 'react';

import {
  faDownload,
  faPhone
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Badge,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Table,
  Text,
  Title
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';

import { FormatPhone } from '../utils/format';
import { usePostJSON } from '../utils/request';

interface EncounterSummaryRow {
  callerId: number;
  callerFirstName: string | null;
  callerLastName: string | null;
  callerPhone: string;
  encounterCount: number;
}

interface SummaryResponse {
  success: boolean;
  data: EncounterSummaryRow[];
}

const EncounterReport: React.FC = () => {
  const tz = moment.tz.guess();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    moment().subtract(1, 'week').startOf('isoWeek').toDate(),
    moment().subtract(1, 'week').endOf('isoWeek').toDate(),
  ]);

  const start = dateRange[0] ? moment(dateRange[0]).format('YYYY-MM-DD') : null;
  const end = dateRange[1] ? moment(dateRange[1]).format('YYYY-MM-DD') : null;

  const csvFilename = `encounter-summary-${start}-to-${end}.csv`;

  // Helper function to check if date range matches a preset
  const matchesRange = (presetStart: Date, presetEnd: Date): boolean => {
    if (!dateRange[0] || !dateRange[1]) return false;
    return (
      moment(dateRange[0]).format('YYYY-MM-DD') === moment(presetStart).format('YYYY-MM-DD') &&
      moment(dateRange[1]).format('YYYY-MM-DD') === moment(presetEnd).format('YYYY-MM-DD')
    );
  };

  const { data: response, error, loading } = usePostJSON<SummaryResponse>(
    start && end ? '/api/report/encounter_summary' : '',
    { start, end, tz },
    [start, end]
  );

  const data = response?.data ?? null;

  const [downloadingSummary, setDownloadingSummary] = useState(false);
  const [downloadingDetails, setDownloadingDetails] = useState(false);

  const downloadSummary = async () => {
    setDownloadingSummary(true);
    try {
      const res = await fetch('/api/report/encounter_summary/csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start, end, tz }),
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = csvFilename;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingSummary(false);
    }
  };

  const downloadDetails = async () => {
    setDownloadingDetails(true);
    try {
      const res = await fetch('/api/report/encounter_details/csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start, end, tz }),
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `encounter-details-${start}-to-${end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingDetails(false);
    }
  };

  return (
    <Stack gap="lg">
      <Title order={1}>Encounter Report</Title>

      <Stack gap="sm">
        <DatePickerInput
          firstDayOfWeek={0}
          placeholder="Select date range"
          size="md"
          type="range"
          value={dateRange}
          onChange={(value) => setDateRange(value as [Date | null, Date | null])}
        />
        <Group gap="xs">
          <Button
            size="xs"
            variant={matchesRange(
              moment().startOf('isoWeek').toDate(),
              moment().endOf('isoWeek').toDate()
            ) ? 'filled' : 'light'}
            onClick={() => setDateRange([
              moment().startOf('isoWeek').toDate(),
              moment().endOf('isoWeek').toDate(),
            ])}
          >
            This Week
          </Button>
          <Button
            size="xs"
            variant={matchesRange(
              moment().subtract(1, 'week').startOf('isoWeek').toDate(),
              moment().subtract(1, 'week').endOf('isoWeek').toDate()
            ) ? 'filled' : 'light'}
            onClick={() => setDateRange([
              moment().subtract(1, 'week').startOf('isoWeek').toDate(),
              moment().subtract(1, 'week').endOf('isoWeek').toDate(),
            ])}
          >
            Last Week
          </Button>
          <Button
            size="xs"
            variant={matchesRange(
              moment().startOf('month').toDate(),
              moment().endOf('month').toDate()
            ) ? 'filled' : 'light'}
            onClick={() => setDateRange([
              moment().startOf('month').toDate(),
              moment().endOf('month').toDate(),
            ])}
          >
            This Month
          </Button>
          <Button
            size="xs"
            variant={matchesRange(
              moment().subtract(1, 'month').startOf('month').toDate(),
              moment().subtract(1, 'month').endOf('month').toDate()
            ) ? 'filled' : 'light'}
            onClick={() => setDateRange([
              moment().subtract(1, 'month').startOf('month').toDate(),
              moment().subtract(1, 'month').endOf('month').toDate(),
            ])}
          >
            Last Month
          </Button>
        </Group>
      </Stack>

      {error && (
        <Alert color="red" title="Error">
          {error.message}
        </Alert>
      )}

      {data && (
        <Paper withBorder p="md" style={{ position: 'relative' }}>
          <LoadingOverlay visible={loading} />

          <Group justify="space-between" mb="md">
            <Title order={3}>Results</Title>
            <Badge color="blue" variant="light">
              {data.length} {data.length === 1 ? 'caller' : 'callers'}
            </Badge>
          </Group>

          {data.length === 0 ? (
            <Text c="dimmed" py="xl" ta="center">
              No encounters found for this date range.
            </Text>
          ) : (
            <Table highlightOnHover striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Phono ID</Table.Th>
                  <Table.Th>First Name</Table.Th>
                  <Table.Th>Last Name</Table.Th>
                  <Table.Th>Phone</Table.Th>
                  <Table.Th>Encounters</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.map((row) => (
                  <Table.Tr key={row.callerId}>
                    <Table.Td>
                      <Text
                        component={Link}
                        fw={500}
                        to={`/caller/edit/${row.callerId}`}
                      >
                        {row.callerId}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500}>{row.callerFirstName ?? ''}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500}>{row.callerLastName ?? ''}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <FontAwesomeIcon icon={faPhone} size="sm" />
                        <Text size="sm">{FormatPhone(row.callerPhone)}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge color="blue" variant="light">
                        {row.encounterCount}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      )}

      {data && data.length > 0 && (
        <Group>
          <Button
            leftSection={<FontAwesomeIcon icon={faDownload} />}
            loading={downloadingSummary}
            size="md"
            variant="light"
            onClick={() => void downloadSummary()}
          >
            Download Summary
          </Button>
          <Button
            leftSection={<FontAwesomeIcon icon={faDownload} />}
            loading={downloadingDetails}
            size="md"
            variant="light"
            onClick={() => void downloadDetails()}
          >
            Download Details
          </Button>
        </Group>
      )}
    </Stack>
  );
};

export default EncounterReport;
